import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import AuthContext from '../../contexts/AuthContext';
import { useToast } from '../../Toast';
import lavenderIconLight from '../../assets/blue_unfilled_icon_light.png';
import lavenderIconDark from '../../assets/lavender_unfilled_icon_dark.png';

const API_URL = '/api';

/**
 * Projects component - Main project list view
 * Displays all projects (owned and collaborative), allows creating/editing/deleting
 */
function Projects() {
  const [projects, setProjects] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const { logout, darkMode, toggleDarkMode, user } = React.useContext(AuthContext);
  const toast = useToast();

  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    try {
      const response = await axios.get(`${API_URL}/projects`);
      setProjects(response.data);
    } catch (err) {
      console.error('Failed to load projects', err);
    }
  };

  const createProject = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/projects`, { name, description });
      setName('');
      setDescription('');
      setShowCreate(false);
      loadProjects();
      toast.success('Project created successfully!');
    } catch (err) {
      console.error('Create project error:', err.response?.data || err.message);
      toast.error('Failed to create project: ' + (err.response?.data?.error || err.message));
    }
  };

  const deleteProject = async (projectId, projectName, e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm(`Are you sure you want to delete "${projectName}"? This will delete all references in this project.`)) {
      return;
    }
    try {
      await axios.delete(`${API_URL}/projects/${projectId}`);
      toast.success(`Project "${projectName}" deleted successfully`);
      loadProjects();
    } catch (err) {
      toast.error('Failed to delete project: ' + (err.response?.data?.error || 'Unknown error'));
    }
  };

  const duplicateProject = async (projectId, projectName, e) => {
    e.preventDefault();
    e.stopPropagation();
    const newName = prompt(`Enter name for duplicated project:`, `${projectName} (Copy)`);
    if (!newName) return;

    try {
      await axios.post(`${API_URL}/projects/${projectId}/duplicate`, { name: newName });
      toast.success(`Project duplicated as "${newName}"`);
      loadProjects();
    } catch (err) {
      toast.error('Failed to duplicate project: ' + (err.response?.data?.error || 'Unknown error'));
    }
  };

  const startEdit = (project, e) => {
    e.preventDefault();
    e.stopPropagation();
    setEditingProject(project.id);
    setName(project.name);
    setDescription(project.description || '');
  };

  const updateProject = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API_URL}/projects/${editingProject}`, { name, description });
      setName('');
      setDescription('');
      setEditingProject(null);
      toast.success('Project updated successfully');
      loadProjects();
    } catch (err) {
      toast.error('Failed to update project: ' + (err.response?.data?.error || 'Unknown error'));
    }
  };

  const cancelEdit = () => {
    setEditingProject(null);
    setName('');
    setDescription('');
  };

  return (
    <div className="container">
      <div className="header">
        <h1>
          <img src={darkMode ? lavenderIconDark : lavenderIconLight} alt="Ref-Lex Icon" style={{ height: '2.7em', verticalAlign: 'bottom', marginRight: '0.3em' }} />
          <span className="logo-text">Ref-Lex</span>
        </h1>
        <div className="header-actions">
          {user?.is_admin && (
            <Link to="/admin" className="admin-link-btn">Admin</Link>
          )}
          <button onClick={toggleDarkMode} className="theme-toggle" title="Toggle dark mode">
            {darkMode ? '☀️' : '🕶️'}
          </button>
          <Link to="/account" className="account-btn" title="Account Settings">
            👤
          </Link>
          <button onClick={logout} className="logout-btn">Logout</button>
        </div>
      </div>

      <div className="projects-section">
        <div className="section-header">
          <h2>Projects</h2>
          <button onClick={() => setShowCreate(!showCreate)}>
            {showCreate ? 'Cancel' : 'New Project'}
          </button>
        </div>

        {showCreate && (
          <form onSubmit={createProject} className="create-form">
            <input
              type="text"
              placeholder="Project Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
            <textarea
              placeholder="Description (optional)"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
            <button type="submit">Create</button>
          </form>
        )}

        {editingProject && (
          <form onSubmit={updateProject} className="create-form">
            <h3>Edit Project</h3>
            <input
              type="text"
              placeholder="Project Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
            <textarea
              placeholder="Description (optional)"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
            <div className="form-actions">
              <button type="submit">Update</button>
              <button type="button" onClick={cancelEdit}>Cancel</button>
            </div>
          </form>
        )}

        <div className="projects-grid">
          {projects.map(project => (
            <Link
              key={project.id}
              to={`/projects/${project.id}`}
              className="project-card"
            >
              <div className="project-card-header">
                <h3>{project.name}</h3>
                {project.is_owner && (
                  <div className="project-actions">
                    <button
                      className="edit-project-btn"
                      onClick={(e) => startEdit(project, e)}
                      title="Edit project"
                    >
                      ✎
                    </button>
                    <button
                      className="duplicate-project-btn"
                      onClick={(e) => duplicateProject(project.id, project.name, e)}
                      title="Duplicate project"
                    >
                      ⎘
                    </button>
                    <button
                      className="delete-project-btn"
                      onClick={(e) => deleteProject(project.id, project.name, e)}
                      title="Delete project"
                    >
                      ✕
                    </button>
                  </div>
                )}
              </div>
              <p>{project.description || 'No description'}</p>
              <span className="badge">{project.is_owner ? 'Owner' : 'Collaborator'}</span>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

export default Projects;
